import React, { useState, useEffect } from 'react';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './components/ui/Toast';
import { ErrorBoundary } from './components/ErrorBoundary';

// Public Core Components (Eagerly loaded for instant homepage render)
import { PublicHeader } from './components/public/PublicHeader';
import { PublicFooter } from './components/public/PublicFooter';
import { HomePage } from './pages/public/HomePage';
import { isCrmPageSlug } from './data/crmPageSlugs';
import type { FeatureKey } from './pages/public/FeatureDetailPage';
import type { SocialPlatform } from '../../shared/types';

// Lazy-loaded Public Secondary Pages
const FeaturesPage = React.lazy(() => import('./pages/public/FeaturesPage').then(m => ({ default: m.FeaturesPage })));
const HowItWorksPage = React.lazy(() => import('./pages/public/HowItWorksPage').then(m => ({ default: m.HowItWorksPage })));
const PricingPage = React.lazy(() => import('./pages/public/PricingPage').then(m => ({ default: m.PricingPage })));
const IntegrationsPage = React.lazy(() => import('./pages/public/IntegrationsPage').then(m => ({ default: m.IntegrationsPage })));
const PlatformDetailPage = React.lazy(() => import('./pages/public/PlatformDetailPage').then(m => ({ default: m.PlatformDetailPage })));
const UseCasesPage = React.lazy(() => import('./pages/public/UseCasesPage').then(m => ({ default: m.UseCasesPage })));
const BlogListPage = React.lazy(() => import('./pages/public/BlogListPage').then(m => ({ default: m.BlogListPage })));
const BlogPostPage = React.lazy(() => import('./pages/public/BlogPostPage').then(m => ({ default: m.BlogPostPage })));
const FAQPage = React.lazy(() => import('./pages/public/FAQPage').then(m => ({ default: m.FAQPage })));
const AboutPage = React.lazy(() => import('./pages/public/AboutPage').then(m => ({ default: m.AboutPage })));
const ContactPage = React.lazy(() => import('./pages/public/ContactPage').then(m => ({ default: m.ContactPage })));
const PrivacyPolicyPage = React.lazy(() => import('./pages/public/PrivacyPolicyPage').then(m => ({ default: m.PrivacyPolicyPage })));
const DisclaimerPage = React.lazy(() => import('./pages/public/DisclaimerPage').then(m => ({ default: m.DisclaimerPage })));
const TermsPage = React.lazy(() => import('./pages/public/TermsPage').then(m => ({ default: m.TermsPage })));
const GeoLandingPage = React.lazy(() => import('./pages/public/GeoLandingPage').then(m => ({ default: m.GeoLandingPage })));
const NotFoundPage = React.lazy(() => import('./pages/public/NotFoundPage').then(m => ({ default: m.NotFoundPage })));
const FeatureDetailPage = React.lazy(() => import('./pages/public/FeatureDetailPage').then(m => ({ default: m.FeatureDetailPage })));
const MultiChannelAnalyticsPage = React.lazy(() => import('./pages/public/MultiChannelAnalyticsPage').then(m => ({ default: m.MultiChannelAnalyticsPage })));
const AIContentStudioPage = React.lazy(() => import('./pages/public/AIContentStudioPage').then(m => ({ default: m.AIContentStudioPage })));
const OneClickPublishingPage = React.lazy(() => import('./pages/public/OneClickPublishingPage').then(m => ({ default: m.OneClickPublishingPage })));
const SmartSchedulingPage = React.lazy(() => import('./pages/public/SmartSchedulingPage').then(m => ({ default: m.SmartSchedulingPage })));
const BrandMemoryProfilesPage = React.lazy(() => import('./pages/public/BrandMemoryProfilesPage').then(m => ({ default: m.BrandMemoryProfilesPage })));
const MediaCloudDriveSyncPage = React.lazy(() => import('./pages/public/MediaCloudDriveSyncPage').then(m => ({ default: m.MediaCloudDriveSyncPage })));
const ClientWorkspaceIsolationPage = React.lazy(() => import('./pages/public/ClientWorkspaceIsolationPage').then(m => ({ default: m.ClientWorkspaceIsolationPage })));
const MarketingDigitalAgenciesPage = React.lazy(() => import('./pages/public/MarketingDigitalAgenciesPage').then(m => ({ default: m.MarketingDigitalAgenciesPage })));
const ContentCreatorsInfluencersPage = React.lazy(() => import('./pages/public/ContentCreatorsInfluencersPage').then(m => ({ default: m.ContentCreatorsInfluencersPage })));
const B2BEnterpriseTeamsPage = React.lazy(() => import('./pages/public/B2BEnterpriseTeamsPage').then(m => ({ default: m.B2BEnterpriseTeamsPage })));
const McpApiV2Page = React.lazy(() => import('./pages/public/McpApiV2Page').then(m => ({ default: m.McpApiV2Page })));
const CrmLandingPage = React.lazy(() => import('./pages/public/CrmLandingPage').then(m => ({ default: m.CrmLandingPage })));
const VerifyEmailPage = React.lazy(() => import('./pages/public/VerifyEmailPage').then(m => ({ default: m.VerifyEmailPage })));

// Lazy-loaded Scale Bulk WhatsApp Suite Pages
const WhatsAppHubPage = React.lazy(() => import('./pages/public/whatsapp/WhatsAppHubPage').then(m => ({ default: m.WhatsAppHubPage })));
const WhatsAppSharedInboxPage = React.lazy(() => import('./pages/public/whatsapp/WhatsAppSharedInboxPage').then(m => ({ default: m.WhatsAppSharedInboxPage })));
const WhatsAppBulkMessagingPage = React.lazy(() => import('./pages/public/whatsapp/WhatsAppBulkMessagingPage').then(m => ({ default: m.WhatsAppBulkMessagingPage })));
const WhatsAppMultiAgentPage = React.lazy(() => import('./pages/public/whatsapp/WhatsAppMultiAgentPage').then(m => ({ default: m.WhatsAppMultiAgentPage })));
const WhatsAppAutomationPage = React.lazy(() => import('./pages/public/whatsapp/WhatsAppAutomationPage').then(m => ({ default: m.WhatsAppAutomationPage })));
const WhatsAppLeadManagementPage = React.lazy(() => import('./pages/public/whatsapp/WhatsAppLeadManagementPage').then(m => ({ default: m.WhatsAppLeadManagementPage })));
const WhatsAppBusinessApiPage = React.lazy(() => import('./pages/public/whatsapp/WhatsAppBusinessApiPage').then(m => ({ default: m.WhatsAppBusinessApiPage })));
const WhatsAppCrmIntegrationPage = React.lazy(() => import('./pages/public/whatsapp/WhatsAppCrmIntegrationPage').then(m => ({ default: m.WhatsAppCrmIntegrationPage })));
const WhatsAppMarketingAnalyticsPage = React.lazy(() => import('./pages/public/whatsapp/WhatsAppMarketingAnalyticsPage').then(m => ({ default: m.WhatsAppMarketingAnalyticsPage })));
const WhatsAppCrmPage = React.lazy(() => import('./pages/public/whatsapp/WhatsAppCrmPage').then(m => ({ default: m.WhatsAppCrmPage })));

// Lazy-loaded Authenticated Application Shell (Defers all dashboard infrastructure & providers)
const AuthenticatedApp = React.lazy(() => import('./AuthenticatedApp').then(m => ({ default: m.AuthenticatedApp })));

// Lazy-loaded Authentication Modal (Only loaded when user clicks sign-in/register)
const AuthModal = React.lazy(() => import('./pages/AuthModal').then(m => ({ default: m.AuthModal })));

const PageFallback: React.FC = () => (
  <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <div style={{ width: '32px', height: '32px', borderRadius: '50%', border: '2.5px solid rgba(99, 102, 241, 0.2)', borderTopColor: '#6366f1', animation: 'spin 0.8s linear infinite' }} />
  </div>
);

const DASHBOARD_TABS = new Set([
  'dashboard', 'composer', 'ai-studio', 'calendar', 'bulk-import', 
  'social-accounts', 'scheduled', 'published', 'drafts', 'media', 
  'media-library', 'storage', 'analytics', 'campaigns', 'team', 
  'clients', 'brand-profile', 'billing', 'admin', 'seo-intelligence'
]);

const AppContent: React.FC = () => {
  const { user } = useAuth();
  const [currentPath, setCurrentPath] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      const raw = window.location.pathname || '/';
      return raw.length > 1 ? raw.replace(/\/+$/, '') : raw;
    }
    return '/';
  });

  const [authModal, setAuthModal] = useState<{
    isOpen: boolean;
    mode: 'login' | 'register' | 'otp_verify' | 'forgot_password' | 'reset_password';
  }>({ isOpen: false, mode: 'login' });

  // Auto-redirect from /login or /signin to /dashboard if user is already authenticated
  useEffect(() => {
    if (user && (currentPath === '/login' || currentPath === '/signin')) {
      const target = (user.is_superadmin || user.role === 'admin' || user.role === 'owner') ? '/dashboard#admin' : '/dashboard';
      navigateTo(target);
    }
  }, [user, currentPath]);

  // Auto-prompt login modal when directly visiting /login or /signin while unauthenticated
  useEffect(() => {
    if (!user && (currentPath === '/login' || currentPath === '/signin')) {
      setAuthModal(prev => prev.isOpen ? prev : { isOpen: true, mode: 'login' });
    }
  }, [user, currentPath]);

  // Synchronize browser history navigation & hash tab routing
  useEffect(() => {
    const handleUrlChange = () => {
      const raw = window.location.pathname || '/';
      const clean = raw.length > 1 ? raw.replace(/\/+$/, '') : raw;
      setCurrentPath(clean);
    };
    window.addEventListener('popstate', handleUrlChange);
    window.addEventListener('hashchange', handleUrlChange);
    return () => {
      window.removeEventListener('popstate', handleUrlChange);
      window.removeEventListener('hashchange', handleUrlChange);
    };
  }, []);

  const navigateTo = (path: string) => {
    const [pathPart, hashPart] = path.split('#');
    const cleanPath = pathPart && pathPart.length > 1 ? pathPart.replace(/\/+$/, '') : (pathPart || '/');
    const finalUrl = hashPart ? `${cleanPath}#${hashPart}` : cleanPath;
    window.history.pushState({}, '', finalUrl);
    setCurrentPath(cleanPath);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Determine if viewing authenticated dashboard
  const isRootHome = currentPath === '/' || currentPath === '';
  const rawHash = typeof window !== 'undefined' ? window.location.hash.replace(/^#/, '') : '';
  const isDashboardHash = !isRootHome && (DASHBOARD_TABS.has(rawHash) || rawHash === 'media-library');
  const pathSegment = currentPath.replace(/^\//, '').replace(/\/.*$/, '');
  const isDirectDashboardTab = !isRootHome && pathSegment !== '' && DASHBOARD_TABS.has(pathSegment);
  const isDashboardTarget = currentPath === '/dashboard' || currentPath.startsWith('/dashboard') || currentPath.startsWith('/app') || isDirectDashboardTab || isDashboardHash;

  // If user is targeting dashboard view, delegate entirely to AuthenticatedApp shell
  if (isDashboardTarget) {
    return (
      <React.Suspense fallback={<PageFallback />}>
        <AuthenticatedApp
          onNavigateHome={() => navigateTo('/')}
          onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })}
        />
        {authModal.isOpen && (
          <React.Suspense fallback={null}>
            <AuthModal
              isOpen={authModal.isOpen}
              initialMode={authModal.mode}
              onNavigate={navigateTo}
              onClose={() => setAuthModal({ isOpen: false, mode: 'login' })}
            />
          </React.Suspense>
        )}
      </React.Suspense>
    );
  }

  // Render Public SaaS Marketing Website
  const renderPublicPage = () => {
    // 1. Home
    if (currentPath === '/' || currentPath === '') {
      return <HomePage onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }

    // Direct Email Verification Route
    if (currentPath === '/verify-email') {
      return <VerifyEmailPage onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }

    // Direct Password Reset Route
    if (currentPath === '/reset-password') {
      if (!authModal.isOpen) {
        setAuthModal({ isOpen: true, mode: 'reset_password' });
      }
    }

    // Direct Forgot Password Route
    if (currentPath === '/forgot-password') {
      if (!authModal.isOpen) {
        setAuthModal({ isOpen: true, mode: 'forgot_password' });
      }
    }

    // Direct Auth Route: /register, /signup
    if (currentPath === '/register' || currentPath === '/signup') {
      return (
        <div style={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
          <div className="liquid-glass-panel" style={{ padding: '48px 36px', maxWidth: '480px', width: '100%', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-lg)', textAlign: 'center' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'linear-gradient(135deg, #6366f1, #ec4899)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)' }}>
              <span style={{ fontSize: '1.4rem' }}>⚡</span>
            </div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 900, marginBottom: '8px', color: 'var(--text-primary)' }}>Create Your Agency Account</h2>
            <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '24px' }}>Join hundreds of marketing agencies publishing across all major social networks.</p>
            <button
              onClick={() => setAuthModal({ isOpen: true, mode: 'register' })}
              className="btn-magic"
              style={{ width: '100%', padding: '12px', fontSize: '0.95rem', justifyContent: 'center' }}
            >
              <span>Open Registration Form</span>
            </button>
          </div>
        </div>
      );
    }

    // Direct Auth Route: /login, /signin
    if (currentPath === '/login' || currentPath === '/signin') {
      return (
        <div style={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 24px' }}>
          <div className="liquid-glass-panel" style={{ padding: '48px 36px', maxWidth: '480px', width: '100%', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-lg)', textAlign: 'center' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: 'linear-gradient(135deg, #6366f1, #ec4899)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)' }}>
              <span style={{ fontSize: '1.4rem' }}>⚡</span>
            </div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 900, marginBottom: '8px', color: 'var(--text-primary)' }}>Welcome to Scale Bulk</h2>
            <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '24px' }}>Sign in to manage channels, schedule posts, or connect Meta Facebook.</p>
            <button
              onClick={() => setAuthModal({ isOpen: true, mode: 'login' })}
              className="btn-magic"
              style={{ width: '100%', padding: '12px', fontSize: '0.95rem', justifyContent: 'center' }}
            >
              <span>Open Sign In / 1-Click Demo</span>
            </button>
          </div>
        </div>
      );
    }

    // 2. Features Overview & Specific Feature Detail Pages
    if (currentPath === '/features') {
      return <FeaturesPage onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }
    if (currentPath === '/features/ai-content-studio' || currentPath === '/features/ai-content') {
      return <AIContentStudioPage onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }
    if (currentPath === '/features/one-click-publishing' || currentPath === '/features/publishing') {
      return <OneClickPublishingPage onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }
    if (currentPath === '/features/smart-scheduling' || currentPath === '/features/scheduling') {
      return <SmartSchedulingPage onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }
    if (currentPath === '/features/multi-channel-analytics' || currentPath === '/features/analytics') {
      return <MultiChannelAnalyticsPage onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }
    if (currentPath === '/features/brand-memory-profiles' || currentPath === '/features/brand-memory') {
      return <BrandMemoryProfilesPage onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }
    if (currentPath === '/features/media-cloud-drive-sync' || currentPath === '/features/media-cloud' || currentPath === '/features/drive-sync') {
      return <MediaCloudDriveSyncPage onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }
    if (currentPath.startsWith('/features/')) {
      const featureKey = currentPath.replace('/features/', '') as FeatureKey;
      return <FeatureDetailPage featureKey={featureKey} onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }

    // 3. How It Works
    if (currentPath === '/how-it-works') {
      return <HowItWorksPage onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }

    // 4. Pricing
    if (currentPath === '/pricing') {
      return <PricingPage onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }

    // 4b. Dedicated Scale Bulk WhatsApp Suite Routes
    if (currentPath === '/whatsapp') {
      return <WhatsAppHubPage onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }
    if (currentPath === '/whatsapp/shared-inbox' || currentPath === '/whatsapp-shared-inbox') {
      return <WhatsAppSharedInboxPage onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }
    if (currentPath === '/whatsapp/bulk-messaging' || currentPath === '/whatsapp-bulk-messaging' || currentPath === '/bulk-whatsapp-messaging') {
      return <WhatsAppBulkMessagingPage onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }
    if (currentPath === '/whatsapp/multi-agent' || currentPath === '/multi-agent-whatsapp' || currentPath === '/multi-agent-whatsapp-inbox') {
      return <WhatsAppMultiAgentPage onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }
    if (currentPath === '/whatsapp/automation' || currentPath === '/whatsapp-automation' || currentPath === '/whatsapp-crm-automation') {
      return <WhatsAppAutomationPage onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }
    if (currentPath === '/whatsapp/lead-management' || currentPath === '/whatsapp-lead-management') {
      return <WhatsAppLeadManagementPage onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }
    if (currentPath === '/whatsapp/business-api' || currentPath === '/whatsapp-business-api') {
      return <WhatsAppBusinessApiPage onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }
    if (currentPath === '/whatsapp/crm-integration' || currentPath === '/whatsapp-crm-integration') {
      return <WhatsAppCrmIntegrationPage onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }
    if (currentPath === '/whatsapp/marketing-analytics' || currentPath === '/whatsapp-analytics' || currentPath === '/whatsapp-marketing-analytics') {
      return <WhatsAppMarketingAnalyticsPage onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }
    if (currentPath === '/whatsapp/crm' || currentPath === '/whatsapp-crm') {
      return <WhatsAppCrmPage onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }

    // 4c. Long-tail & Regional ScaleBulk CRM landing pages
    const cleanCrmPath = currentPath.replace(/^\//, '').replace(/\/$/, '');
    if (isCrmPageSlug(cleanCrmPath)) {
      return <CrmLandingPage slug={cleanCrmPath} onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }

    // Developer Platform & MCP API v2
    if (currentPath === '/developers/mcp-api-v2' || currentPath === '/developers/mcp' || currentPath === '/mcp-api-v2' || currentPath === '/developers') {
      return <McpApiV2Page onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }

    // 5. Integrations Overview
    if (currentPath === '/integrations') {
      return <IntegrationsPage onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }

    // 6. Platform Specific Detail Pages (/integrations/instagram, /integrations/linkedin, etc.)
    if (currentPath.startsWith('/integrations/')) {
      const platformKey = currentPath.replace('/integrations/', '') as SocialPlatform;
      return <PlatformDetailPage platform={platformKey} onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }

    // 7. Solutions & Use Cases Overview & Audience Pages
    if (
      currentPath === '/solutions/content-creators-influencers' ||
      currentPath === '/solutions/content-creators' ||
      currentPath === '/solutions/creators' ||
      currentPath === '/solutions/creators-influencers'
    ) {
      return <ContentCreatorsInfluencersPage onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }
    if (
      currentPath === '/solutions/marketing-digital-agencies' ||
      currentPath === '/solutions/marketing-agencies' ||
      currentPath === '/solutions/digital-agencies' ||
      currentPath === '/solutions/agency-client-workspaces'
    ) {
      return <MarketingDigitalAgenciesPage onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }
    if (currentPath === '/solutions/client-workspace-isolation' || currentPath === '/solutions/client-workspaces') {
      return <ClientWorkspaceIsolationPage onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }
    if (
      currentPath === '/solutions/b2b-enterprise-teams' ||
      currentPath === '/solutions/enterprise' ||
      currentPath === '/solutions/b2b-enterprise' ||
      currentPath === '/solutions/b2b'
    ) {
      return <B2BEnterpriseTeamsPage onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }
    if (currentPath === '/solutions' || currentPath === '/use-cases') {
      return <UseCasesPage onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }
    if (currentPath.startsWith('/solutions/')) {
      const solutionKey = currentPath.replace('/solutions/', '') as any;
      return <UseCasesPage audience={solutionKey} onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }
    if (currentPath.startsWith('/use-cases/')) {
      const audienceKey = currentPath.replace('/use-cases/', '') as any;
      return <UseCasesPage audience={audienceKey} onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }

    // 8. Blog & Resources CMS List & Post Pages
    if (currentPath === '/blog' || currentPath === '/resources') {
      return <BlogListPage onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }
    if (currentPath.startsWith('/blog/')) {
      const slug = currentPath.replace('/blog/', '');
      return <BlogPostPage slug={slug} onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }
    if (currentPath.startsWith('/resources/')) {
      const slug = currentPath.replace('/resources/', '');
      return <BlogPostPage slug={slug} onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }

    // 9. FAQ & Knowledge Base
    if (currentPath === '/faq' || currentPath === '/knowledge-base') {
      return <FAQPage onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }

    // 10. About
    if (currentPath === '/about') {
      return <AboutPage onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }

    // 11. Contact
    if (currentPath === '/contact') {
      return <ContactPage onNavigate={navigateTo} />;
    }

    // 12. Legal Pages
    if (currentPath === '/privacy-policy') {
      return <PrivacyPolicyPage onNavigate={navigateTo} />;
    }
    if (currentPath === '/disclaimer') {
      return <DisclaimerPage onNavigate={navigateTo} />;
    }
    if (currentPath === '/terms-and-conditions' || currentPath === '/terms') {
      return <TermsPage onNavigate={navigateTo} />;
    }

    // 13. Regional GEO Landing Pages (/social-media-management-usa, uk, india, uae) & City Pages (/social-media-management/dubai, /social-media-management/jaipur)
    if (currentPath.startsWith('/social-media-management-')) {
      const regionKey = currentPath.replace('/social-media-management-', '') as any;
      return <GeoLandingPage regionKey={regionKey} onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }
    if (currentPath.startsWith('/social-media-management/')) {
      const cityKey = currentPath.replace('/social-media-management/', '') as any;
      return <GeoLandingPage regionKey={cityKey} onNavigate={navigateTo} onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })} />;
    }

    // 14. 404 Fallback
    return <NotFoundPage onNavigate={navigateTo} />;
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-primary)', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
      {/* Sticky Header with Logo & Dropdown */}
      <PublicHeader
        currentPath={currentPath}
        onNavigate={navigateTo}
        onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })}
        isAuthenticated={Boolean(user)}
      />

      {/* Dynamic Public Route Viewport */}
      <main style={{ flex: 1, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <React.Suspense fallback={<PageFallback />}>
          {renderPublicPage()}
        </React.Suspense>
      </main>

      {/* Multi-Column Footer with Pre-Footer CTA */}
      <PublicFooter
        onNavigate={navigateTo}
        onOpenAuth={(mode) => setAuthModal({ isOpen: true, mode })}
      />

      {/* Authentication Modal - Lazy loaded */}
      {authModal.isOpen && (
        <React.Suspense fallback={null}>
          <AuthModal
            isOpen={authModal.isOpen}
            initialMode={authModal.mode}
            onNavigate={navigateTo}
            onClose={() => setAuthModal({ isOpen: false, mode: 'login' })}
          />
        </React.Suspense>
      )}
    </div>
  );
};

export const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <ToastProvider>
            <AppContent />
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
